import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Dialog } from '@/components/ui/dialog';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { FIELD_CLASS } from '@/components/properties/AddressFields';
import { useAccount } from '@/api/useAccount';
import { useCurrentUser } from '@/auth/useCurrentUser';
import { formatInstantWithZone } from '@/lib/format';
import { SettingsNav } from '@/routes/settings/SettingsNav';
import {
  USER_ROLES,
  UserRefusedError,
  useChangeUserRole,
  useDeactivateUser,
  useDeactivationPreview,
  useInviteUser,
  useReactivateUser,
  useResendInvite,
  useRevokeInvite,
  useUserRoster,
  type UserRole,
  type UserSummary,
} from '@/api/useUsers';

type Open =
  | { kind: 'invite' }
  | { kind: 'role'; user: UserSummary }
  | { kind: 'revoke'; user: UserSummary }
  | { kind: 'deactivate'; user: UserSummary }
  | null;

/**
 * The account's users (M2-20, pass B), at `/settings/users`: everyone, their
 * role and where they stand, and every change the API offers — invite,
 * resend, revoke, change role, deactivate, reactivate. `users.manage` (Owner
 * and Admin), gated by the route.
 *
 * <b>What the caller cannot do is not offered</b> (Kyle, 2026-10-08): an Admin
 * sees no action on an Owner's row and no Owner in any role list; nobody is
 * offered Deactivate on their own row or on someone still Invited (revoke is
 * the act there). The API enforces all of it regardless, and when it refuses
 * — the last active Owner, the seat cap, anything a stale screen got wrong —
 * its own sentence is what the person reads.
 *
 * Mobile-first: a stacked list, not a table, so nothing scrolls sideways at
 * 375 px and every action is a full-size button.
 */
export function UsersSettings() {
  const { t } = useTranslation();
  const roster = useUserRoster();
  const me = useCurrentUser().data;
  const timeZone = useAccount().data?.timezone;
  const headingId = useId();

  const [open, setOpen] = useState<Open>(null);
  const [outcome, setOutcome] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const outcomeRef = useRef<HTMLParagraphElement>(null);

  const resend = useResendInvite();
  const reactivate = useReactivateUser();

  // After an action the button that started it is often gone (Revoke becomes
  // "Send new invitation", Deactivate becomes Reactivate), and the dialog
  // hands focus back to it: focus would fall to the page's body (M2-19's
  // finding). It lands on the sentence that says what happened instead.
  useEffect(() => {
    if (outcome) outcomeRef.current?.focus();
  }, [outcome]);

  const isOwner = me?.role === 'Owner';
  const roles = USER_ROLES.filter((role) => isOwner || role !== 'Owner');
  const canTouch = (user: UserSummary) => isOwner || user.role !== 'Owner';

  function done(message: string) {
    setOpen(null);
    setFailure(null);
    setOutcome(message);
  }

  function sendNewInvite(user: UserSummary) {
    setOutcome(null);
    setFailure(null);
    resend.mutate(user.id, {
      onSuccess: () => done(t('users.outcome.resent', { name: user.name })),
      onError: (error) => setFailure(refusalText(error, t)),
    });
  }

  function restore(user: UserSummary) {
    setOutcome(null);
    setFailure(null);
    reactivate.mutate(user.id, {
      onSuccess: () => done(t('users.outcome.reactivated', { name: user.name })),
      onError: (error) => setFailure(refusalText(error, t)),
    });
  }

  return (
    <section className="space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight">{t('nav.settings')}</h1>
      <SettingsNav />

      <section className="space-y-4" aria-labelledby={headingId}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 id={headingId} className="text-lg font-semibold">
              {t('users.heading')}
            </h2>
            <p className="text-sm text-[var(--color-muted-foreground)]">{t('users.description')}</p>
          </div>
          <Button
            type="button"
            onClick={() => {
              setOutcome(null);
              setOpen({ kind: 'invite' });
            }}
          >
            {t('users.invite.open')}
          </Button>
        </div>

        <p
          ref={outcomeRef}
          tabIndex={-1}
          role="status"
          aria-live="polite"
          data-testid="users-outcome"
          className="text-sm focus:outline-none"
        >
          {outcome ?? ''}
        </p>
        {failure ? (
          <p role="alert" data-testid="users-failure" className="text-sm text-[var(--color-destructive)]">
            {failure}
          </p>
        ) : null}

        {roster.isPending ? (
          <LoadingState label={t('users.loading')} />
        ) : roster.isError ? (
          <ErrorState title={t('users.loadErrorTitle')} onRetry={() => void roster.refetch()} />
        ) : (
          <ul className="divide-y divide-[var(--color-border)] rounded-lg border" data-testid="users-list">
            {roster.data.map((user) => (
              <li key={user.id} className="space-y-2 p-4" data-testid="user-row">
                <div className="min-w-0">
                  <p className="font-medium break-words">
                    {user.name}
                    {user.id === me?.userId ? (
                      <span className="text-xs font-normal text-[var(--color-muted-foreground)]">
                        {' '}
                        {t('users.you')}
                      </span>
                    ) : null}
                  </p>
                  <p className="text-sm break-all text-[var(--color-muted-foreground)]">{user.email}</p>
                </div>
                <p className="text-sm">
                  <span data-testid="user-role">{t(`users.role.${user.role}`, { defaultValue: user.role })}</span>
                  {' · '}
                  <span data-testid="user-status">{statusText(user, timeZone, t)}</span>
                </p>
                {canTouch(user) ? (
                  <div className="flex flex-wrap gap-2">
                    {user.status === 'Invited' && user.inviteState === 'Pending' ? (
                      <>
                        <ActionButton
                          label={t('users.actions.resend')}
                          name={user.name}
                          disabled={resend.isPending}
                          onClick={() => sendNewInvite(user)}
                        />
                        <ActionButton
                          label={t('users.actions.revoke')}
                          name={user.name}
                          onClick={() => {
                            setOutcome(null);
                            setOpen({ kind: 'revoke', user });
                          }}
                        />
                      </>
                    ) : user.status === 'Invited' ? (
                      <ActionButton
                        label={t('users.actions.sendNew')}
                        name={user.name}
                        disabled={resend.isPending}
                        onClick={() => sendNewInvite(user)}
                      />
                    ) : null}
                    {user.status === 'Disabled' ? (
                      <ActionButton
                        label={t('users.actions.reactivate')}
                        name={user.name}
                        disabled={reactivate.isPending}
                        onClick={() => restore(user)}
                      />
                    ) : null}
                    <ActionButton
                      label={t('users.actions.changeRole')}
                      name={user.name}
                      onClick={() => {
                        setOutcome(null);
                        setOpen({ kind: 'role', user });
                      }}
                    />
                    {user.status === 'Active' && user.id !== me?.userId ? (
                      <ActionButton
                        label={t('users.actions.deactivate')}
                        name={user.name}
                        onClick={() => {
                          setOutcome(null);
                          setOpen({ kind: 'deactivate', user });
                        }}
                      />
                    ) : null}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      {open?.kind === 'invite' ? (
        <InviteDialog
          roles={roles}
          onClose={() => setOpen(null)}
          onInvited={(user) => done(t('users.outcome.invited', { name: user.name, email: user.email }))}
        />
      ) : null}
      {open?.kind === 'role' ? (
        <RoleDialog
          user={open.user}
          roles={roles}
          callerId={me?.userId}
          onClose={() => setOpen(null)}
          onChanged={(user) =>
            done(t('users.outcome.roleChanged', { name: user.name, role: t(`users.role.${user.role}`, { defaultValue: user.role }) }))
          }
        />
      ) : null}
      {open?.kind === 'revoke' ? (
        <RevokeDialog
          user={open.user}
          onClose={() => setOpen(null)}
          onRevoked={() => done(t('users.outcome.revoked', { name: open.user.name }))}
        />
      ) : null}
      {open?.kind === 'deactivate' ? (
        <DeactivateDialog
          user={open.user}
          onClose={() => setOpen(null)}
          onDeactivated={(unassigned) =>
            done(t('users.outcome.deactivated', { name: open.user.name, count: unassigned }))
          }
        />
      ) : null}
    </section>
  );
}

type T = ReturnType<typeof useTranslation>['t'];

/** Where someone stands. An invitation's expiry is on the ACCOUNT's clock, date and time together. */
function statusText(user: UserSummary, timeZone: string | undefined, t: T): string {
  if (user.status === 'Active') return t('users.status.active');
  if (user.status === 'Disabled') return t('users.status.disabled');
  if (user.status === 'Invited') {
    if (user.inviteState === 'Pending' && user.inviteExpiresAt) {
      return t('users.status.pending', { when: formatInstantWithZone(user.inviteExpiresAt, timeZone) });
    }
    if (user.inviteState === 'Expired') return t('users.status.expired');
    if (user.inviteState === 'Revoked') return t('users.status.revoked');
    return t('users.status.invited');
  }
  return user.status;
}

/** The API's sentence when it gave one; the Owner rule's 403 (no body) in ours. */
function refusalText(error: unknown, t: T): string {
  if (error instanceof UserRefusedError) {
    if (error.forbidden) return t('users.ownersOnly');
    if (error.message) return error.message;
  }
  return t('users.failed');
}

/** A row's action: the visible word, and the person's name for a screen reader (label-in-name). */
function ActionButton({
  label,
  name,
  disabled,
  onClick,
}: {
  label: string;
  name: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <Button type="button" variant="outline" size="sm" aria-label={`${label}: ${name}`} disabled={disabled} onClick={onClick}>
      {label}
    </Button>
  );
}

function RolePicker({
  id,
  roles,
  value,
  onChange,
  invalid,
  describedBy,
}: {
  id: string;
  roles: readonly UserRole[];
  value: UserRole | '';
  onChange: (role: UserRole | '') => void;
  invalid?: boolean;
  describedBy?: string;
}) {
  const { t } = useTranslation();
  return (
    <select
      id={id}
      className={FIELD_CLASS}
      value={value}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      onChange={(event) => onChange(event.target.value as UserRole | '')}
    >
      {/* No default (Kyle, 2026-10-08): the inviter chooses, every time. */}
      <option value="" disabled>
        {t('users.chooseRole')}
      </option>
      {roles.map((role) => (
        <option key={role} value={role}>
          {t(`users.role.${role}`)}
        </option>
      ))}
    </select>
  );
}

function FieldError({ id, message }: { id: string; message: string | undefined }) {
  return message ? (
    <p id={id} className="mt-1 text-xs text-[var(--color-destructive)]">
      {message}
    </p>
  ) : null;
}

/** The API's own words for one field of a 400, matched case-insensitively (it keys them by property). */
function fieldError(error: unknown, field: string): string | undefined {
  if (!(error instanceof UserRefusedError)) return undefined;
  const entry = Object.entries(error.fieldErrors).find(([key]) => key.toLowerCase() === field.toLowerCase());
  return entry?.[1]?.[0];
}

function InviteDialog({
  roles,
  onClose,
  onInvited,
}: {
  roles: readonly UserRole[];
  onClose: () => void;
  onInvited: (user: UserSummary) => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const invite = useInviteUser();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole | ''>('');
  const [roleMissing, setRoleMissing] = useState(false);

  function submit() {
    if (role === '') {
      setRoleMissing(true);
      return;
    }
    invite.mutate({ name, email, role }, { onSuccess: onInvited });
  }

  const nameError = fieldError(invite.error, 'Name');
  const emailError = fieldError(invite.error, 'Email');
  const roleError = roleMissing && role === '' ? t('users.invite.roleRequired') : fieldError(invite.error, 'Role');
  const general =
    invite.error && !nameError && !emailError && !fieldError(invite.error, 'Role') ? refusalText(invite.error, t) : null;

  return (
    <Dialog
      titleId={`${id}-title`}
      title={t('users.invite.title')}
      description={t('users.invite.description')}
      onClose={onClose}
      testId="invite-dialog"
      footer={
        <>
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="button" size="sm" disabled={invite.isPending} onClick={submit}>
            {t('users.invite.send')}
          </Button>
        </>
      }
    >
      <div className="text-sm">
        <label htmlFor={`${id}-name`} className="mb-1 block font-medium">
          {t('users.invite.name')}
        </label>
        <input
          id={`${id}-name`}
          className={FIELD_CLASS}
          value={name}
          autoComplete="off"
          aria-invalid={nameError ? true : undefined}
          aria-describedby={nameError ? `${id}-name-error` : undefined}
          onChange={(event) => setName(event.target.value)}
        />
        <FieldError id={`${id}-name-error`} message={nameError} />
      </div>
      <div className="text-sm">
        <label htmlFor={`${id}-email`} className="mb-1 block font-medium">
          {t('users.invite.email')}
        </label>
        <input
          id={`${id}-email`}
          type="email"
          className={FIELD_CLASS}
          value={email}
          autoComplete="off"
          aria-invalid={emailError ? true : undefined}
          aria-describedby={emailError ? `${id}-email-error` : undefined}
          onChange={(event) => setEmail(event.target.value)}
        />
        <FieldError id={`${id}-email-error`} message={emailError} />
      </div>
      <div className="text-sm">
        <label htmlFor={`${id}-role`} className="mb-1 block font-medium">
          {t('users.invite.role')}
        </label>
        <RolePicker
          id={`${id}-role`}
          roles={roles}
          value={role}
          onChange={setRole}
          invalid={Boolean(roleError)}
          describedBy={roleError ? `${id}-role-error` : undefined}
        />
        <FieldError id={`${id}-role-error`} message={roleError} />
      </div>
      {general ? (
        <p role="alert" className="text-sm text-[var(--color-destructive)]">
          {general}
        </p>
      ) : null}
    </Dialog>
  );
}

function RoleDialog({
  user,
  roles,
  callerId,
  onClose,
  onChanged,
}: {
  user: UserSummary;
  roles: readonly UserRole[];
  callerId: string | undefined;
  onClose: () => void;
  onChanged: (user: UserSummary) => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const change = useChangeUserRole(callerId);
  const [role, setRole] = useState<UserRole | ''>(user.role as UserRole);

  return (
    <Dialog
      titleId={`${id}-title`}
      title={t('users.roleDialog.title', { name: user.name })}
      onClose={onClose}
      testId="role-dialog"
      footer={
        <>
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={change.isPending || role === '' || role === user.role}
            onClick={() => role !== '' && change.mutate({ id: user.id, role }, { onSuccess: onChanged })}
          >
            {t('users.roleDialog.save')}
          </Button>
        </>
      }
    >
      <div className="text-sm">
        <label htmlFor={`${id}-role`} className="mb-1 block font-medium">
          {t('users.invite.role')}
        </label>
        <RolePicker id={`${id}-role`} roles={roles} value={role} onChange={setRole} />
      </div>
      {change.isError ? (
        <p role="alert" className="text-sm text-[var(--color-destructive)]">
          {refusalText(change.error, t)}
        </p>
      ) : null}
    </Dialog>
  );
}

function RevokeDialog({ user, onClose, onRevoked }: { user: UserSummary; onClose: () => void; onRevoked: () => void }) {
  const { t } = useTranslation();
  const id = useId();
  const revoke = useRevokeInvite();
  return (
    <ConfirmDialog
      titleId={`${id}-title`}
      testId="revoke-dialog"
      title={t('users.revokeDialog.title', { name: user.name })}
      description={t('users.revokeDialog.description')}
      confirmLabel={t('users.revokeDialog.confirm')}
      isPending={revoke.isPending}
      errorMessage={revoke.isError ? refusalText(revoke.error, t) : undefined}
      onConfirm={() => revoke.mutate(user.id, { onSuccess: onRevoked })}
      onClose={onClose}
    />
  );
}

/**
 * Deactivating takes their upcoming visits off them in the same transaction,
 * so the dialog says how many first, from the API's own count
 * (`GET /users/{id}/deactivation`, the same rule the deactivation applies).
 * Confirm waits for the count.
 */
function DeactivateDialog({
  user,
  onClose,
  onDeactivated,
}: {
  user: UserSummary;
  onClose: () => void;
  onDeactivated: (unassigned: number) => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const preview = useDeactivationPreview(user.id);
  const deactivate = useDeactivateUser();

  // A refetch counts as counting: the cached number is from an earlier opening.
  const counting = preview.isPending || preview.isFetching;
  const description = counting
    ? t('users.deactivateDialog.counting')
    : preview.isError
      ? `${t('users.deactivateDialog.access')} ${t('users.deactivateDialog.countFailed')}`
      : `${t('users.deactivateDialog.access')} ${t('users.deactivateDialog.visits', { count: preview.data })}`;

  return (
    <ConfirmDialog
      titleId={`${id}-title`}
      testId="deactivate-dialog"
      title={t('users.deactivateDialog.title', { name: user.name })}
      description={description}
      confirmLabel={t('users.deactivateDialog.confirm')}
      isPending={counting || deactivate.isPending}
      errorMessage={deactivate.isError ? refusalText(deactivate.error, t) : undefined}
      onConfirm={() => deactivate.mutate(user.id, { onSuccess: ({ unassigned }) => onDeactivated(unassigned) })}
      onClose={onClose}
    />
  );
}
